import React, { useEffect, useState, useRef } from 'react';
import { Send, X, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChatMessage } from '../types';
import { getBotResponse, INITIAL_MESSAGE } from '../utils/chatResponses';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/Button';
interface ChatWindowProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ChatWindow({
  isOpen,
  onClose
}: ChatWindowProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const handleSend = (text: string = input) => {
    if (!text.trim()) return;
    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      text,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    // Simulate bot thinking
    setTimeout(() => {
      const botMsg = getBotResponse(text);
      setMessages(prev => [...prev, botMsg]);
    }, 600);
  };
  const handleAction = (action: {
    type: string;
    value: string;
    label: string;
  }) => {
    if (action.type === 'link') {
      navigate(action.value);
      if (window.innerWidth < 768) onClose(); // Close on mobile nav
    } else {
      handleSend(action.label);
    }
  };
  return <AnimatePresence data-id="element-52">
      {isOpen && <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} transition={{
      duration: 0.2
    }} className="fixed bottom-20 right-4 w-[350px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[70vh] bg-dark-card border border-gray-800 rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden" data-id="element-53">
          {/* Header */}
          <div className="bg-primary p-4 flex justify-between items-center" data-id="element-54">
            <div className="flex items-center" data-id="element-55">
              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center mr-3" data-id="element-56">
                <ShoppingBag className="text-primary w-5 h-5" data-id="element-57" />
              </div>
              <div data-id="element-58">
                <h3 className="font-retro text-white text-sm" data-id="element-59">
                  Short Stop Bot
                </h3>
                <p className="text-primary-light text-xs" data-id="element-60">Ask us anything!</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white" data-id="element-61">
              <X size={20} data-id="element-62" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-dark" data-id="element-63">
            {messages.map(msg => <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-64">
                <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${msg.sender === 'user' ? 'bg-primary text-white rounded-tr-none' : 'bg-gray-800 text-gray-200 rounded-tl-none'}`} data-id="element-65">
                  <p data-id="element-66">{msg.text}</p>
                  {msg.actions && <div className="mt-3 flex flex-wrap gap-2" data-id="element-67">
                      {msg.actions.map((action, idx) => <button key={idx} onClick={() => handleAction(action)} className="text-xs bg-black/20 hover:bg-black/40 px-2 py-1 rounded-md transition-colors border border-white/10" data-id="element-68">
                          {action.label}
                        </button>)}
                    </div>}
                </div>
              </div>)}
            <div ref={messagesEndRef} data-id="element-69" />
          </div>

          {/* Input */}
          <div className="p-3 bg-dark-card border-t border-gray-800" data-id="element-70">
            <form onSubmit={e => {
          e.preventDefault();
          handleSend();
        }} className="flex gap-2" data-id="element-71">
              <input type="text" value={input} onChange={e => setInput(e.target.value)} placeholder="Type a message..." className="flex-1 bg-dark-lighter border border-gray-700 rounded-full px-4 py-2 text-sm text-white focus:outline-none focus:border-primary" data-id="element-72" />
              <Button type="submit" variant="primary" className="rounded-full w-10 h-10 p-0 flex items-center justify-center" disabled={!input.trim()} data-id="element-73">
                <Send size={16} data-id="element-74" />
              </Button>
            </form>
          </div>
        </motion.div>}
    </AnimatePresence>;
}